import { cn } from "@/lib/utils";
import { setShowAddEditProjectModal } from "@/store/projectsSlice";
import { EditorContext, useCodebaseStore } from "@/store/zustand/codebaseStore";
import useLayoutStore, { LeftTab } from "@/store/zustand/layoutStore";
import { track } from "@/utils/events/tracker";
import {
  BookOpenIcon,
  ComponentIcon,
  FileIcon,
  GitBranchIcon,
  HistoryIcon,
  InfoIcon,
  LayersIcon,
  Settings,
  Store,
} from "lucide-react";
import { useFeatureFlagEnabled } from "posthog-js/react";
import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import HistoryPanel from "../ActivityStream/HistoryPanel";
import { Chat } from "../AI/chat/Chat";
import { AssetsPanel } from "../AssetsPanel/AssetsPanel";
import CommunityPanel from "../Community/CommunityPanel";
import Icon, { ICONS } from "../Core/Icons";
import AppStore from "../LeftNav/AppStore";
import Convex from "../LeftNav/Convex";
import PagesPane from "../LeftNav/PagesPane";
import FileSelector from "../LeftNav/FileSelector";
import GitSettings from "../LeftNav/GitSettings";
import InfoPanel from "../LeftNav/Info";
import LocalEditing from "../LeftNav/LocalEditing";
import SupabaseConnect from "../LeftNav/SupabaseConnect";
import ProjectSettings from "../settings";
import { Button } from "../ui/button";

const AiLogoIcon = (props: any) => (
  <Icon
    icon={ICONS.AI_LOGO}
    width={20}
    height={20}
    {...props}
    className={cn(
      "cursor-pointer text-white p-1 w-auto h-6 rounded-sm bg-primary-0 hover:bg-primary-100",
      props.selected && "bg-primary-100",
    )}
  />
);

const SupabaseLogoIcon = (props: any) => (
  <img src="/images/supabase-logo-icon.svg" alt="Supabase" {...props} />
);

const LocalEditorIcon = (props: any) => {
  const icon = <img src="/images/vscode-icon.svg" alt="VSCode" {...props} />;

  return (
    <div className="relative">
      <div className={"transition-opacity duration-200 opacity-100"}>
        {icon}
      </div>
    </div>
  );
};

const ConvexLogoIcon = (props: any) => (
  <img
    src="/images/convex.png"
    alt="Convex"
    {...props}
    className={props.className}
  />
);

const FigmaLogoIcon = (props: any) => (
  <img
    src="/images/figma-logo.svg"
    alt="Figma"
    {...props}
    className={props.className}
  />
);

interface TabContentProps {
  activeTab: LeftTab;
  tab: LeftTab | LeftTab[];
  children: React.ReactNode;
}

const TabContent: React.FC<TabContentProps> = ({
  activeTab,
  tab,
  children,
}) => {
  const isActive = Array.isArray(tab)
    ? tab.includes(activeTab)
    : tab === activeTab;

  return (
    <div className={isActive ? "grow h-full hide-scrollbar min-w-0" : "hidden"}>
      {children}
    </div>
  );
};

const Tabs = React.memo(() => {
  const gitStatus = useSelector((state: any) => state.gitStatus.gitStatus);
  const setEditorContext = useCodebaseStore((state) => state.setEditorContext);

  const leftTab = useLayoutStore((state) => state.leftTab);
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);

  const conditions = useCodebaseStore((state) => state.conditions);

  // Check if the project is using a Convex template
  const isConvexTemplate = Boolean(conditions["convexInstalled"]?.met);

  const allowCommunity = useFeatureFlagEnabled("community");

  const icons: any = [
    {
      tab: LeftTab.AIChat,
      IconEl: AiLogoIcon,
      alt: "AI Icon",
      tooltip: "AI Chat",
      selected: leftTab === LeftTab.AIChat,
    },
    "SEPARATOR",
    {
      tab: LeftTab.DomTree,
      IconEl: LayersIcon,
      alt: "Dom Nav Icon",
      tooltip: "Pages",
      selected: leftTab === LeftTab.DomTree,
    },
    {
      tab: LeftTab.Assets,
      IconEl: ComponentIcon,
      alt: "Assets Icon",
      tooltip: "Assets",
      selected: leftTab === LeftTab.Assets,
    },
    ...(allowCommunity
      ? [
          {
            tab: LeftTab.Community,
            IconEl: BookOpenIcon,
            alt: "Community Icon",
            tooltip: "Community",
            selected: leftTab === LeftTab.Community,
          },
        ]
      : []),
    "SEPARATOR",
    {
      tab: LeftTab.FileSelector,
      IconEl: FileIcon,
      alt: "File Selector Icon",
      tooltip: "File Selector",
      selected: leftTab === LeftTab.FileSelector,
    },
    {
      tab: LeftTab.Git,
      IconEl: GitBranchIcon,
      alt: "Git Settings Icon",
      tooltip: "Git Settings",
      selected: leftTab === LeftTab.Git,
    },
    {
      tab: LeftTab.History,
      IconEl: HistoryIcon,
      alt: "History Icon",
      tooltip: "History",
      selected: leftTab === LeftTab.History,
    },
    {
      tab: LeftTab.AppStore,
      IconEl: Store,
      alt: "App Store",
      tooltip: "App Store",
      selected: leftTab === LeftTab.AppStore,
    },
    ...(isConvexTemplate
      ? [
          {
            tab: LeftTab.Convex,
            IconEl: ConvexLogoIcon,
            alt: "Convex Icon",
            tooltip: "Convex",
            selected: leftTab === LeftTab.Convex,
          },
        ]
      : []),
    // Only show Supabase connection tab if not using a Convex template
    ...(!isConvexTemplate
      ? [
          {
            tab: LeftTab.SupabaseConnect,
            IconEl: SupabaseLogoIcon,
            alt: "Connect Supabase",
            tooltip: "Connect Supabase",
            selected: leftTab === LeftTab.SupabaseConnect,
          },
        ]
      : []),
    {
      tab: LeftTab.FigmaExtension,
      IconEl: FigmaLogoIcon,
      alt: "Figma Extension",
      tooltip: "Figma Extension",
      selected: leftTab === LeftTab.FigmaExtension,
    },
    {
      tab: LeftTab.LocalEditing,
      IconEl: LocalEditorIcon,
      alt: "Local Editing",
      tooltip: "Local Editing",
      selected: leftTab === LeftTab.LocalEditing,
    },
    {
      tab: LeftTab.Info,
      IconEl: InfoIcon,
      alt: "Info Icon",
      tooltip: "Info",
      selected: leftTab === LeftTab.Info,
    },
    {
      tab: LeftTab.ProjectSettings,
      IconEl: Settings,
      alt: "Project settings Icon",
      tooltip: "Project Settings",
      selected:
        leftTab === LeftTab.ProjectSettings ||
        leftTab === LeftTab.ProjectSettingsAdvanced,
    },
  ];

  useEffect(() => {
    if (leftTab === LeftTab.EngSpec) {
      setEditorContext(EditorContext.EngSpec);
    } else if (leftTab === LeftTab.History) {
      setEditorContext(EditorContext.History);
    } else {
      setEditorContext(EditorContext.Code);
    }
  }, [leftTab]);

  const uncommittedChangedFiles =
    gitStatus?.status?.files?.map((file) => file.path) || [];

  return (
    <aside
      className="shrink-0 w-12 flex overflow-hidden flex-col align-center justify-between min-h-0 border-r"
      data-testid="left-panel-tabs"
    >
      <ul className="items-center flex flex-col gap-1 grow-0 pt-2">
        {icons.map((icon, index) => {
          if (icon === "SEPARATOR") {
            return (
              <div
                className="bg-secondary-300 h-[2px] w-[calc(100%-10px)] my-1 block"
                key={`separator-${index}`}
              />
            );
          }

          const IconEl = icon.IconEl;

          const onClickFn = () => {
            // prevent switching to null tab when opening figma
            if (icon.tab == LeftTab.FigmaExtension) {
              track("FIGMA_LINK");
              window.open(
                "https://www.figma.com/community/plugin/1463689183126672406/tempo-labs",
                "_blank",
              );
              return;
            }

            if (leftTab !== icon.tab) {
              // @ts-expect-error TS2345
              setNewLayout(icon.tab, null);
            }
          };

          return (
            <li key={icon.tab}>
              <Button
                variant="ghost"
                size="icon"
                data-testid={`left-panel-${icon.tab.toLowerCase().replace(/\s+/g, "-")}-tab`}
                className={cn(
                  "p-0 relative items-center text-icons-muted justify-center h-8 w-8 rounded-lg",
                  icon.selected
                    ? "bg-accent text-accent-foreground"
                    : undefined,
                )}
                onClick={onClickFn}
              >
                <IconEl
                  data-tooltip-id="tooltip"
                  data-tooltip-place="right"
                  data-tooltip-content={icon.tooltip}
                  className="h-5 w-5 outline-hidden"
                />
                {icon.tab === LeftTab.Git && uncommittedChangedFiles?.length ? (
                  <div className="absolute -top-1 -right-1 w-4 h-4 bg-primary-0 text-3xs flex justify-center items-center rounded-sm text-center text-foreground">
                    {uncommittedChangedFiles.length}
                  </div>
                ) : null}
              </Button>
            </li>
          );
        })}
      </ul>
    </aside>
  );
});

Tabs.displayName = "Tabs";

const LeftPanel = React.memo(() => {
  const leftTab = useLayoutStore((state) => state.leftTab);
  const setNewLayout = useLayoutStore((state) => state.setNewLayout);
  const allowCommunity = useFeatureFlagEnabled("community");

  const showAddEditProjectModal = useSelector(
    (state: any) => state.projects.showAddEditProjectModal,
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (showAddEditProjectModal) {
      setNewLayout(LeftTab.ProjectSettings, undefined);
      dispatch(setShowAddEditProjectModal(false));
    }
  }, [showAddEditProjectModal, setNewLayout, dispatch]);

  return (
    <div
      className="relative h-full w-full flex flex-row z-1001"
      data-testid="left-panel"
    >
      <Tabs />
      {/* Tab Content */}
      <TabContent activeTab={leftTab} tab={[LeftTab.AIChat, LeftTab.EngSpec]}>
        <Chat />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.DomTree}>
        <PagesPane />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.Assets}>
        <AssetsPanel />
      </TabContent>
      {allowCommunity && (
        <TabContent activeTab={leftTab} tab={LeftTab.Community}>
          <CommunityPanel isActiveTab={leftTab === LeftTab.Community} />
        </TabContent>
      )}
      <TabContent activeTab={leftTab} tab={LeftTab.FileSelector}>
        <FileSelector />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.Git}>
        <GitSettings />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.History}>
        <HistoryPanel />
      </TabContent>
      <TabContent
        activeTab={leftTab}
        tab={[LeftTab.ProjectSettings, LeftTab.ProjectSettingsAdvanced]}
      >
        <ProjectSettings
          defaultAdvancedOpen={leftTab === LeftTab.ProjectSettingsAdvanced}
        />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.SupabaseConnect}>
        <SupabaseConnect />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.LocalEditing}>
        <LocalEditing />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.Info}>
        <InfoPanel />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.Convex}>
        <Convex />
      </TabContent>
      <TabContent activeTab={leftTab} tab={LeftTab.AppStore}>
        <AppStore />
      </TabContent>
    </div>
  );
});

LeftPanel.displayName = "LeftPanel";

export default LeftPanel;
